import React, { useState } from "react";
import { countryFlagUrl, countryName } from "@utils/countries";
import "./country-flag.css";

/**
 * A country's flag, and optionally its name beside it.
 *
 * Country was only ever shown as a word — on the case list, the case
 * detail and the dashboard breakdown — which is slow to scan when every
 * other row says something different. The flag is the fast read; the
 * name stays because a flag alone is a guessing game.
 *
 * Drawn from an image, not the regional-indicator emoji: Chrome on
 * Windows ships no flag glyphs and renders "🇬🇧" as the letters GB, so
 * the emoji version would show nothing on most of the desks this runs
 * on. If the image itself cannot load (offline, blocked CDN) the code
 * takes its place in the same footprint, so the row never reflows.
 *
 * Props:
 *   code      ISO 3166-1 alpha-2, e.g. "GB". Unknown or missing → no flag.
 *   showName  render the country's name after the flag (default true)
 *   fallback  what the name reads when there is no code
 *   size      flag width in px (default 18); height follows 3:2
 */
const CountryFlag = ({ code, showName = true, fallback = "Not set", size = 18 }) => {
  const [failed, setFailed] = useState(false);
  const src = countryFlagUrl(code);
  const name = countryName(code, fallback);
  const known = !!src;

  return (
    <span className="country-cell">
      { known &&
        (failed
          ? <span className="country-flag is-fallback" style={ { width: size, height: Math.round(size * 2 / 3) } }>
            { String(code).toUpperCase() }
          </span>
          : <img
            className="country-flag"
            style={ { width: size, height: Math.round(size * 2 / 3) } }
            src={ src }
            alt=""
            loading="lazy"
            onError={ () => setFailed(true) }
          />) }
      { showName &&
        <span className={ known ? "country-name" : "country-name is-unknown" }>{ name }</span> }
    </span>
  );
};

export default CountryFlag;
